import React, { useMemo } from "react";

// 3rd Party libraries
import { Badge, IconButton, TextField } from "@mui/material";

// Components
import Author from "components/Table/Author";
import MDBox from "components/MDBox";
import CustomImage from "components/Table/GroupImage";
import Status from "components/Table/Status";

// Utils
import Constants, { Icons, Colors, Common } from "utils/Constants";
import pxToRem from "assets/theme/functions/pxToRem";

const getPreviousRequestedQty = (quantityHistory = []) => {
  if (!Array.isArray(quantityHistory) || quantityHistory.length === 0) return null;

  const sortedHistory = [...quantityHistory].sort(
    (a, b) => new Date(a?.updatedAt || 0).getTime() - new Date(b?.updatedAt || 0).getTime()
  );
  const latestEntry = sortedHistory[sortedHistory.length - 1];
  const oldQty = latestEntry?.oldQty;

  return Number.isFinite(Number(oldQty)) ? Number(oldQty) : null;
};

const ADJUSTMENT_BUTTON_HIDDEN_STATUSES = new Set(Common.PROJECT_ORDER_ADJUSTMENT_BLOCKED_STATUSES);

const normalizeStatus = (status = "") =>
  `${status || ""}`.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");

export default function ProjectOrderHistoryDetailsData({
  dataList = [],
  handleViewDetailsClick,
  openWarehouseCommentModal,
  openRejectCommentModal,
  showOrderAdjustActions = false,
  adjustmentQuantityMap = {},
  handleAdjustmentQuantity = () => {},
  handleAdjustmentQuantityInput = () => {},
  adjustmentLoading = false,
}) {
  const projectHistoryByIdRows = useMemo(() => {
    if (dataList.length === 0) return [];

    return dataList.map((item, index) => {
      const previousRequestedQty = getPreviousRequestedQty(item?.quantityHistory);
      const approvedReqQtyText =
        previousRequestedQty > 0
          ? `${item?.pmRequestedQuantity ?? 0} (Prev ${previousRequestedQty})`
          : `${item?.pmRequestedQuantity ?? 0}`;
      const hideAdjustmentButtons = ADJUSTMENT_BUTTON_HIDDEN_STATUSES.has(
        normalizeStatus(item?.status)
      );

      return {
        srNo: <Author name={index + 1} />,
        equipmentType: (
          <MDBox display="flex" justifyContent="start" alignItems="center">
            <CustomImage item={item?.equipmentTypeImage?.url} width={30} height={30} />
            <MDBox
              style={{
                flex: 1,
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Author name={item?.typeName} />
            </MDBox>
            {item?.isTemporary && (
              <MDBox>
                <IconButton aria-label="fingerprint">{Icons.CLOCK}</IconButton>
              </MDBox>
            )}
          </MDBox>
        ),
        approvedRequestedQuantity: <Author name={approvedReqQtyText} />,
        adjustedRequestedQuantity: (
          <MDBox display="flex" alignItems="center" justifyContent="flex-start">
            {!hideAdjustmentButtons && (
              <IconButton
                color="info"
                onClick={() => handleAdjustmentQuantity(item?.rowKey, "decrement")}
                disabled={adjustmentLoading}
                size="small"
              >
                {Icons.DASH2}
              </IconButton>
            )}
            <TextField
              type="number"
              size="small"
              value={
                adjustmentQuantityMap?.[item?.rowKey]?.adjustedQuantity ??
                Number(item?.pmRequestedQuantity || 0)
              }
              onChange={(e) => handleAdjustmentQuantityInput(item?.rowKey, e?.target?.value)}
              disabled={adjustmentLoading || hideAdjustmentButtons}
              inputProps={{ min: 0, step: 1 }}
              sx={{
                width: pxToRem(72),
                mx: hideAdjustmentButtons ? 0 : 0.5,
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  py: 0.6,
                },
              }}
            />
            {!hideAdjustmentButtons && (
              <IconButton
                color="info"
                onClick={() => handleAdjustmentQuantity(item?.rowKey, "increment")}
                disabled={adjustmentLoading}
                size="small"
              >
                {Icons.NEW}
              </IconButton>
            )}
          </MDBox>
        ),
        warehouseApprovedQuantity: <Author name={item.wmApprovedQuantity} />,
        status: (
          <MDBox display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
            {item?.status === Constants.STATUS_REJECTED &&
              item?.remark?.length > 0 &&
              (item?.remark[0]?.comment || item?.remark[0]?.comments) && (
                <MDBox
                  bgColor={Colors.LIGHT_RED}
                  // mb={1}
                  borderRadius={pxToRem(16)}
                  sx={{ width: pxToRem(30), height: pxToRem(30) }}
                  display="flex"
                  justifyContent="center"
                  align="center"
                >
                  <IconButton
                    aria-label="fingerprint"
                    onClick={() => openRejectCommentModal(Common.USER_TEXT, item?.remark[0])}
                  >
                    {Icons.COMMENT_RED}
                  </IconButton>
                </MDBox>
              )}
            <MDBox sx={{ marginLeft: pxToRem(10) }}>
              <Status title={item?.status ? `${item?.status?.replace("-", " ")}` : ""} />
            </MDBox>
          </MDBox>
        ),
        action: (
          <MDBox display="flex">
            <IconButton
              aria-label="comment"
              color="error"
              disabled={
                item?.comments?.pmComments?.length === 0 && item?.comments?.wmComments?.length === 0
              }
              onClick={() => openWarehouseCommentModal(Common.USER_TEXT, item?.comments)}
            >
              <Badge
                badgeContent={
                  (item?.comments?.pmComments?.length || 0) +
                  (item?.comments?.wmComments?.length || 0)
                }
                color="error"
                max={9}
                sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 15, minWidth: 15 } }}
              >
                {item?.comments?.pmComments?.length === 0 &&
                item?.comments?.wmComments?.length === 0
                  ? Icons.COMMENT_DISABLE
                  : Icons.COMMENT}
              </Badge>
            </IconButton>

            <IconButton
              aria-label="view"
              color="error"
              onClick={() => handleViewDetailsClick(item?.linkedEquipment || [])}
            >
              {Icons.VIEW}
            </IconButton>
          </MDBox>
        ),
      };
    });
  }, [
    dataList,
    adjustmentQuantityMap,
    showOrderAdjustActions,
    handleAdjustmentQuantity,
    handleAdjustmentQuantityInput,
    adjustmentLoading,
  ]);

  const baseColumns = [
    { Header: "No.", accessor: "srNo", width: "3%" },
    { Header: "Equipment Type", accessor: "equipmentType" },
    { Header: "Original Qty", accessor: "approvedRequestedQuantity" },
    { Header: "Status", accessor: "status" },
    { Header: "Action", accessor: "action" },
  ];

  const projectHistoryByIdColumns = showOrderAdjustActions
    ? [
        ...baseColumns.slice(0, 3),
        { Header: "New Requested Qty", accessor: "adjustedRequestedQuantity" },
        { Header: "Warehouse Approved Qty", accessor: "warehouseApprovedQuantity" },
        ...baseColumns.slice(3),
      ]
    : [
        ...baseColumns.slice(0, 3),
        { Header: "Warehouse Approved Qty", accessor: "warehouseApprovedQuantity" },
        ...baseColumns.slice(3),
      ];

  const tableData = {
    projectHistoryByIdColumns,
    projectHistoryByIdRows,
  };

  return tableData;
}
